import { ICONS } from 'components/Icon';
import {
  AssessmentQuestionType,
  DeprecatedActionDue,
  FieldTypeKey,
  TimescaleArray,
  UID,
} from 'types/types';
import {
  EPFieldSettingsGet,
  EPFieldSettingsResultCount,
} from 'types/Endpoints/FieldSettings';
import {
  ActionTimescaleFrom,
  AseSetupSignatory,
  AseTemplateOptionPdfActionsControls,
} from 'types/Assessments';
import { ReportEnum, ReportData } from 'types/Reports';
import { ApiUser, ApiWebhook } from 'types/Api';
import { Engage } from 'types/EngageSettings';
import {
  BillingDiscount,
  BillingItem,
  BillingOrganisation,
  BillingProduct,
  BillingProductUnits,
} from 'types/Billing';
import { SsoOrganisation } from 'types/SsoSettings';
import { EngageStore } from 'types/Engage';

export type OrganisationBasic = {
  id: UID;
  name: string;
};

export const FieldSettingsTypes: Record<
  FieldTypeKey,
  {
    id: FieldTypeKey;
    label: string;
    allowMultiSelect: boolean;
    icon: ICONS;
    iconMultiSelect?: ICONS;
    labelMultiSelect?: string;
    deprecated: boolean;
  }
> = {
  [FieldTypeKey.text]: {
    allowMultiSelect: false,
    id: FieldTypeKey.text,
    label: 'Single Line Text',
    icon: ICONS.InputTextDuo,
    deprecated: true,
  },
  [FieldTypeKey.note]: {
    allowMultiSelect: false,
    id: FieldTypeKey.note,
    label: 'Text',
    icon: ICONS.InputTextareaDuo,
    deprecated: false,
  },
  [FieldTypeKey.number]: {
    allowMultiSelect: false,
    id: FieldTypeKey.number,
    label: 'Number',
    icon: ICONS.InputNumberDuo,
    deprecated: false,
  },
  [FieldTypeKey.options]: {
    allowMultiSelect: true,
    id: FieldTypeKey.options,
    label: 'Single Select List',
    labelMultiSelect: 'Multi Select List',
    icon: ICONS.ListRadioLight,
    iconMultiSelect: ICONS.ListCheckLight,
    deprecated: false,
  },
  [FieldTypeKey.file]: {
    allowMultiSelect: true,
    id: FieldTypeKey.file,
    label: 'File',
    labelMultiSelect: 'Multi Select File',
    icon: ICONS.FileLight,
    deprecated: false,
  },
  [FieldTypeKey.image]: {
    allowMultiSelect: true,
    id: FieldTypeKey.image,
    label: 'Image',
    labelMultiSelect: 'Multi Select Image',
    icon: ICONS.ImageLight,
    deprecated: false,
  },
  [FieldTypeKey.pin]: {
    allowMultiSelect: false,
    id: FieldTypeKey.pin,
    label: 'Pin',
    icon: ICONS.LocationPinLight,
    deprecated: false,
  },
  [FieldTypeKey.qr]: {
    allowMultiSelect: false,
    id: FieldTypeKey.qr,
    label: 'QR',
    icon: ICONS.QRCodeLight,
    deprecated: false,
  },
  [FieldTypeKey.date]: {
    allowMultiSelect: false,
    id: FieldTypeKey.date,
    label: 'Date',
    icon: ICONS.CalendarDayLight,
    deprecated: false,
  },
  [FieldTypeKey.time]: {
    allowMultiSelect: false,
    id: FieldTypeKey.time,
    label: 'Time',
    icon: ICONS.ClockLight,
    deprecated: false,
  },
  [FieldTypeKey.group]: {
    allowMultiSelect: false,
    id: FieldTypeKey.group,
    label: 'Repeatable Group',
    icon: ICONS.LayerGroupLight,
    deprecated: false,
  },
  [FieldTypeKey.grouplight]: {
    allowMultiSelect: false,
    id: FieldTypeKey.grouplight,
    label: 'Repeatable Group',
    icon: ICONS.LayerGroup2Light,
    deprecated: false,
  },
  [FieldTypeKey.checkbox]: {
    allowMultiSelect: false,
    id: FieldTypeKey.checkbox,
    label: 'Yes/No',
    icon: ICONS.ToggleOffLight,
    deprecated: true,
  },
  [FieldTypeKey.divider]: {
    allowMultiSelect: false,
    id: FieldTypeKey.divider,
    label: 'Divider',
    icon: ICONS.FileDashedLineLight,
    deprecated: false,
  },
};

export type FieldSettingsField = {
  id: string;
  name: string;
  childGroupId: string | null;
  multiSelect: boolean;
  type: FieldTypeKey;
  options: { id: string; name: string; countResults: number }[];
  criteria: {
    fieldId: string;
    value: string[];
    operator: 'includes';
  }[];
  suggestions: string[];
  identifier: string;
  identifierEngage: string;
  isLocation: boolean;
};
export type FieldSettingsGroup = {
  fieldIds: string[];
  id: string;
  name: string;
  nameSingular: string;
  repeatable: boolean;
  newCost: number | null;
  existingCost: number | null;
  identifier: string;
  lite: boolean;
  disableAutoId: boolean;
  allowPdfDownload: boolean;
  checks: Record<
    UID,
    {
      id: string;
      name: string;
      typeId: string;
      action: 'create' | 'delete' | null;
    }
  >;
};
export type FieldSettingsSection = {
  managedByOrganisation: { id: UID; name: string } | undefined;
  usedByOrganisations: { id: UID; name: string }[];
  groupIds: string[];
  id: string;
  name: string;
};

type FieldResultCount = EPFieldSettingsResultCount['response']['fields'];

export type APIRequestCreateAsset = {
  name: string;
  organisationId: string;
};
export type APIResponseCreateAsset = {
  assetId: string;
};

export type APIResponseLoadCreateAsset = {
  organisations: [{ id: string; name: string }];
};

type FieldSettingsOpened = {
  sectionId: string | undefined;
  groupId: string | undefined;
  fieldId: string | undefined;
  liteGroupId: string | undefined;
};

export type AssessmentTemplateOpened = {
  templateId: number | undefined;
};

export type AssessmentLibraryOpened = {
  sectionId: number | undefined;
  questionId: number | undefined;
  checklistItemId: number | undefined;
  answerId: number | undefined;
  actionId: number | undefined;
  controlId: number | undefined;
};

export type AssessmentSettings = {
  actionFilter: {
    search: string;
  };
};

export type AssessmentSuggestions = {
  actions: Record<
    string,
    {
      id: number;
      what: string;
      why: string;
      cost: string;
      categoryId: number;
      timescaleId: number;
      severityId: number;
      answerIds: number[];
      positiveChecklistItemIds: number[];
      negativeChecklistItemIds: number[];
      due: DeprecatedActionDue;
    }
  >;
  controls: Record<
    string,
    {
      id: number;
      what: string;
      why: string;
      categoryId: number;
      questionIds: number[];
      positiveChecklistItemIds: number[];
      negativeChecklistItemIds: number[];
    }
  >;
};

export type AssessmentTemplate = {
  id: number;
  name: string;
  title: string;
  description: string;
  enabled: boolean;
  complianceSubtypeId: number;
  schemeId: number;
  assessorApproval: boolean;
  approveOnSubmit: boolean;
  requireAllAnswers: boolean;
  specificationConformsTo: string;
  defaultScope: string;
  actionTimescaleFrom: ActionTimescaleFrom;
  validityModifierId: number | null;
  validityTimescale: TimescaleArray;
  questionComments: boolean;
  addCustomActions: boolean;
  addCustomControls: boolean;
  editAssetName: boolean;
  editAssetAddress: boolean;
  editAssetCustomReference: boolean;
  editAssetAvatar: boolean;
  pdfActionPlan: null | 'dueDate' | 'severity' | 'dueDate2' | 'severity2';
  pdfActionsControls: AseTemplateOptionPdfActionsControls;
  showDashChecklistItems: boolean;
  showActionHistory: boolean;
  showCancelledActions: boolean;
  pdfShowActionSeverities: boolean;
  pdfShowActionTimescales: boolean;
  showControlHistory: boolean;
  showCancelledControls: boolean;
  labelApproved: string;
  pdfIntroduction: string;
  pdfAppendix: string;
  pdfActionPlanText: string;
  pdfContents: boolean;
  pdfIncludeCertificate: boolean;
  pdfPagePhotos: boolean;
  pdfCoverAssessingOrganisation: boolean;
  pdfNewFieldInfoPage: boolean;
  pdfNewFooter: boolean;
  pdfNewFont: boolean;
  pdfH1Numbering: boolean;
  pdfSignatories: boolean;
  pdfSignatoriesTitle: string;
  pdfSignatoriesText: string;
  colourHeader1: string;
  colourHeader2: string;
  colourHeader3: string;
  colourHeaderCover: string;
  pdfId: number;
  flagIds: string[];
  signatoryIds: string[];
  limitOrganisations: boolean;
  visibleToOrganisationIds: UID[];
  allowMultipleAssessors: boolean;
  costs: Record<
    UID,
    {
      cost: number | null;
      minCost: number | null;
      maxCost: number | null;
      fieldGroups: Record<
        string,
        { newCost: number | null; existingCost: number | null }
      >;
    } | null
  >;
  fieldsConfig: Record<
    UID,
    {
      action?: 'delete' | null;
      groups: Record<
        string,
        {
          fieldsInclude: boolean;
          fieldsEditable: boolean;
          fieldsRequired: boolean;
          fieldsIncludeReport: boolean;
          questionIds: number[];
          checks: Record<
            UID,
            {
              include: boolean;
            }
          >;
          fields: Record<
            string,
            {
              include: boolean | null;
              editable: boolean | null;
              required: boolean | null;
              includeReport: boolean | null;
            }
          >;
        }
      >;
    }
  >;
};

export type AssessmentLibrary = {
  folders: Record<
    string,
    {
      id: number;
      name: string;
      ordering: number;
    }
  >;
  sections: Record<
    string,
    {
      id: number;
      name: string;
      description: string;
      reportDescription: string;
      folderId: number;
      ordering: number;
    }
  >;
  questions: Record<
    string,
    {
      id: number;
      question: string;
      description: string;
      reportDescription: string;
      type: AssessmentQuestionType;
      sectionId: number | null;
      answerId: number | null;
      ordering: number;
      templateIds: number[];
      linkedQuestionIds: number[];
    }
  >;
  answers: Record<
    string,
    {
      id: number;
      answer: string;
      description: string;
      reportDescription: string;
      negative: boolean;
      questionId: number;
      ordering: number;
    }
  >;
  checklistItems: Record<
    string,
    {
      id: number;
      text: string;
      questionId: number;
      ordering: number;
    }
  >;
};

export type AssessmentPdf = {
  id: number;
  name: string;
  default: boolean;
};

export type AssessmentFlag = {
  id: string;
  name: string;
  description: string;
  setInProgress: boolean;
  setSubmitted: boolean;
  setApproved: boolean;
  showOnReport: boolean;
  ordering: number;
};

export type AssessmentSignatory = {
  id: string;
  name: string;
  declaration: string;
  requireOnSubmit: boolean;
  allowUnavailable: boolean;
  ordering: number;
};

export type AssessmentScheme = {
  id: number;
  name: string;
  ordering: number;
};

export type AssessmentValidityTimescale = {
  id: number;
  name: string;
  ordering: number;
};

export type ComplianceType = {
  id: number;
  name: string;
};

export type ComplianceSubtype = {
  id: number;
  name: string;
  complianceTypeId: number;
};

export type Categories = Record<
  string,
  {
    id: number;
    name: string;
    groupId: number;
  }
>;

export type CategoryGroups = Record<
  string,
  {
    id: number;
    name: string;
  }
>;

export type Severities = Record<
  string,
  {
    id: number;
    name: string;
    colour: string;
  }
>;

export type Timescales = Record<
  string,
  {
    id: number;
    name: string;
  }
>;

export type DueTypes = Record<
  string,
  {
    id: number;
    name: string;
  }
>;

export type FieldSection = {
  id: number;
  name: string;
  groupIds: number[];
};

export type FieldGroup = {
  id: number;
  name: string;
  fieldIds: number[];
  repeatable: boolean;
  newCost: number | null;
  existingCost: number | null;
  checks: Record<
    UID,
    {
      id: string;
      name: string;
      typeId: string;
    }
  >;
  allowPdfDownload?: boolean;
};

export type FieldField = {
  id: number;
  name: string;
  type: FieldTypeKey;
  multiSelect: boolean;
};

export type FieldGroupCheckType = {
  id: string;
  name: string;
  ordering: number;
};

export type AssessmentTemplateDefaultCost = {
  cost: number;
  minCost: number;
  maxCost: number | null;
};

export enum InvoiceType {
  'invoice' = 'invoice',
  'creditNote' = 'creditNote',
}

export type Invoice = {
  type: InvoiceType;
  id: number;
  reference: string;
  issueDate: number;
  dueDate: number;
  status: 'issued' | 'sent' | 'paid' | 'draft';
  subtotal: number;
  total: number;
};

type BillingStatement = {
  currentBalance: number;
  count: {
    invoices: number;
    payments: number;
    creditNotes: number;
  };
};

type DraftInvoice = {
  name: string;
  key: number;
  code: string;
  issueDate: number;
  subtotal: number;
  total: number;
};

export type AssessmentCheck = {
  id: string;
  name: string;
  type: {
    id: string;
    name: string;
  };
  field: {
    section: { name: string };
    group: { name: string };
  };
  assessmentTypeId: number | null;
};

export enum PageTypeEnum {
  'page' = 'page',
  'report' = 'report',
}

type BasicPage = {
  id: UID | null;
  key: string;
  title: string;
  description: string;
  closeable: boolean;
  current: boolean;
  loaded: boolean;
};

export enum PageEnum {
  'engage' = 'engage',
}

// export type ReportPage = {
//   type: PageTypeEnum.report;
//   key: string;
//   title: string;
//   current: boolean;
//   config: {
//     type: ReportTypeEnum;
//   };
// };
export type ReportPage = BasicPage &
  (
    | {
        type: PageTypeEnum.report;
        config: {
          type: ReportEnum;
        };
        // | {
        //     type: ReportEnum.assets;
        //     flags:
        //       | {
        //           blockManagement: boolean;
        //         }
        //       | undefined;
        //   }
        // | {
        //     type: Exclude<ReportEnum, ReportEnum.assets>;
        //     flags: undefined;
        //   };
      }
    | {
        type: PageTypeEnum.page;
        config: {
          type: PageEnum.engage;
          data: {
            // listData: [];
          };
        };
      }
  );

export type SetupOrganisation = {
  phone: string;
  website: string;
  billingAddress: {
    name: string;
    address1: string;
    address2: string;
    city: string;
    county: string;
    postcode: string;
  };
  billingEmails: string;
  billingEmailsPo: string;
  onBehalfOf: { id: UID; name: string }[];
  approveRequireAddress: boolean;
  approveRequireLogo: boolean;
  approveRequirePhone: boolean;
  approveRequireWebsite: boolean;
  userFeatures: {
    editName: boolean;
    editAddress: boolean;
    editPhone: boolean;
    editWebsite: boolean;
    editBilling: boolean;
    editOnBehalfOf: boolean;
    editApproveRequires: boolean;
    editLogo: boolean;
  };
};

export type RemoteState = {
  isSidebarVisible: boolean;
  usage: number;

  organisationFieldSettings?: EPFieldSettingsGet['response'];
  organisationFieldResultCount?: FieldResultCount;

  organisationId?: string;
  organisation?: OrganisationBasic;
  customers?: Record<string, OrganisationBasic>;

  fieldSettingsOpened: FieldSettingsOpened;
  fieldSectionIdsByOrganisation?: Record<UID, number[]>;
  fieldSections?: Record<string, FieldSection>;
  fieldGroups?: Record<string, FieldGroup>;
  fieldFields?: Record<string, FieldField>;
  fieldGroupCheckTypes?: Record<string, FieldGroupCheckType>;

  assessmentValidityTimescales?: Record<string, AssessmentValidityTimescale>;

  assessmentTemplateOpened: AssessmentTemplateOpened;
  assessmentTemplates?: Record<string, AssessmentTemplate>;
  assessmentTemplateDefaultCosts?: AssessmentTemplateDefaultCost;

  assessmentLibraryOpened: AssessmentLibraryOpened;
  assessmentLibrarySettings: AssessmentSettings;
  assessmentLibrary?: AssessmentLibrary;
  assessmentSuggestions?: AssessmentSuggestions;
  aseLibraryCollapsedSections: number[];

  apiUsers?: ApiUser[];
  apiWebhooks?: ApiWebhook[];

  engage?: Engage;

  ssoSettings?: SsoOrganisation;

  /**
   * Compliance & Checks
   */
  settingsAssessmentChecks?: Record<string, AssessmentCheck>;

  /**
   * Organisations
   */

  setupOrganisation?: SetupOrganisation;

  /**
   * General Data
   */
  assessmentSchemes?: Record<string, AssessmentScheme>;
  assessmentPdfs?: Record<string, AssessmentPdf>;
  assessmentFlags?: Record<string, AssessmentFlag>;
  assessmentSignatories?: Record<string, AseSetupSignatory>;
  complianceTypes?: Record<string, ComplianceType>;
  complianceSubtypes?: Record<string, ComplianceSubtype>;
  categories?: Categories;
  categoryGroups?: CategoryGroups;
  severities?: Severities;
  timescales?: Timescales;
  dueTypes?: DueTypes;

  billingDraftInvoices?: DraftInvoice[];
  billingInvoices?: Invoice[];
  billingStatement?: BillingStatement;
  billingOrganisation?: BillingOrganisation;
  billingProducts?: BillingProduct[];
  billingProductsUnits?: BillingProductUnits[];
  billingItems?: BillingItem[];
  billingDiscounts?: BillingDiscount[];

  reporting: {
    loadStatus: undefined | 'loaded' | 'loading' | 'error';
    reportList: {
      id: string;
      name: string;
      type: ReportEnum;
      typeName: string;
      typeIsDefault: boolean;
      description: string;
    }[];
    reports: Record<string, ReportData>;
    pages: ReportPage[];
  };

  data: Record<string, EngageStore>;
};
